import { isNil } from "lodash";

import { RideStatusIndicatorConst } from "@ryde/controls";
import {
  distanceFormula,
  durationInMinutesCeil,
  formatPrice,
  getAuditLogCauserDisplayName,
  getEventEmoji,
  IRideMetadata,
  IStationInfo,
  NO_VALUE_DISPLAY,
  RideStatusEnum,
} from "@ryde/shared";

import {
  IRideHistoryHeadersGetterArguments,
  RideHistoryHeaderKeys,
} from "./grid.models";

type TRideHistoryHeadersGetter = (
  args: IRideHistoryHeadersGetterArguments,
) => string;

const defaultGetter = (_args: IRideHistoryHeadersGetterArguments): string =>
  NO_VALUE_DISPLAY;

export const rideHistoryHeadersGetters: Record<
  RideHistoryHeaderKeys,
  TRideHistoryHeadersGetter
> = {
  updateTime: ({ item, formatDateTimeWithSecondsNormalized }) =>
    formatDateTimeWithSecondsNormalized(item?.createdAt),
  user: ({ item, translationFn }) =>
    `${getAuditLogCauserDisplayName(item, translationFn) || NO_VALUE_DISPLAY}  ${getEventEmoji(item.eventName)}`,
  rideTime: ({ item, formatDateTimeNormalized }) =>
    item?.fullProperties?.rideStart
      ? formatDateTimeNormalized(item.fullProperties?.rideStart as TDateISO)
      : NO_VALUE_DISPLAY,
  status: ({ item, translationFn: _translationFn, translateLabel }) => {
    const status = item?.fullProperties?.status;
    if (!status) {
      return NO_VALUE_DISPLAY;
    }
    const info = RideStatusIndicatorConst[status as RideStatusEnum];

    return translateLabel(info.$translateKey);
  },
  supplier: ({ item }) =>
    (item?.fullProperties?.supplier as { name: string })?.name ??
    NO_VALUE_DISPLAY,
  vehicleType: ({ item }) =>
    (item?.fullProperties?.vehicleType as { name: string })?.name ??
    NO_VALUE_DISPLAY,
  startStation: ({ item }) => {
    const originStation = item?.fullProperties?.originStation as
      IStationInfo | undefined;

    if (!originStation) {
      return NO_VALUE_DISPLAY;
    }

    return originStation.name;
  },
  endStation: ({ item }) => {
    const destinationStation = item?.fullProperties?.destinationStation as
      IStationInfo | undefined;

    if (!destinationStation) {
      return NO_VALUE_DISPLAY;
    }

    return destinationStation.name;
  },
  passengers: ({ item }) =>
    (item?.fullProperties?.metadata as IRideMetadata)?.passengersCount
      ? String(
          (item?.fullProperties?.metadata as IRideMetadata)?.passengersCount,
        )
      : NO_VALUE_DISPLAY,
  passengerNames: ({ item }) => {
    const passengersInfo = (item?.fullProperties?.metadata as IRideMetadata)
      ?.passengersInfo;

    if (!passengersInfo?.length) {
      return NO_VALUE_DISPLAY;
    }

    return passengersInfo
      .filter(Boolean)
      .map(p => `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.id)
      .join(", ");
  },
  KM: ({ item }) => {
    const totalDistanceMeters = (
      item?.fullProperties?.metadata as IRideMetadata
    )?.totalDistance;

    if (isNil(totalDistanceMeters) || isNaN(totalDistanceMeters)) {
      return NO_VALUE_DISPLAY;
    }

    const result = distanceFormula(totalDistanceMeters);

    if (isNil(result)) {
      return NO_VALUE_DISPLAY;
    }

    return String(result);
  },
  duration: ({ item }) => {
    const totalDuration = (item?.fullProperties?.metadata as IRideMetadata)
      ?.totalDuration;

    if (isNil(totalDuration) || isNaN(totalDuration)) {
      return NO_VALUE_DISPLAY;
    }

    return String(durationInMinutesCeil(totalDuration, false));
  },
  price: ({ item }) => {
    const price = item?.fullProperties?.price as number;
    if (!price || isNaN(price)) {
      return NO_VALUE_DISPLAY;
    }

    return formatPrice({ value: price });
  },
};

export const getGetterByKey = (
  key: RideHistoryHeaderKeys,
): TRideHistoryHeadersGetter => rideHistoryHeadersGetters[key] ?? defaultGetter;

export const checkWasChanged = (
  value: string | null | undefined,
  prevValue: string | null | undefined,
): boolean => {
  const normalizedValue = value?.toLowerCase()?.trim() || "";
  const normalizedPrevValue = prevValue?.toLowerCase()?.trim() || "";

  return normalizedValue !== normalizedPrevValue;
};
